import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { walletApi, type EncashmentStatus, type WalletAdvanceStatus, type ServiceRequestStatus } from "../lib/walletApi";

export const WALLET_KEYS = {
  all: ["wallet"] as const,
  myWallet: (businessId: string) => [...WALLET_KEYS.all, "my", businessId] as const,
  myTransactions: (businessId: string) => [...WALLET_KEYS.all, "my", "txns", businessId] as const,
  myEncashments: (businessId: string) => [...WALLET_KEYS.all, "my", "encs", businessId] as const,
  myAdvances: (businessId: string) => [...WALLET_KEYS.all, "my", "advances", businessId] as const,
  encashments: (businessId: string, status: EncashmentStatus) =>
    [...WALLET_KEYS.all, "admin", "encashments", businessId, status] as const,
  transactions: (businessId: string, prefix?: string) =>
    [...WALLET_KEYS.all, "admin", "txns", businessId, prefix ?? "all"] as const,
  adminAdvances: (businessId: string, status: WalletAdvanceStatus) =>
    [...WALLET_KEYS.all, "admin", "advances", businessId, status] as const,
  walletConfig: (businessId: string) => [...WALLET_KEYS.all, "config", businessId] as const,
  adminCredits: (businessId: string) => [...WALLET_KEYS.all, "admin", "credits", businessId] as const,
  myServiceRequests: (businessId: string) => [...WALLET_KEYS.all, "my", "svcreqs", businessId] as const,
  adminServiceRequests: (businessId: string, status: ServiceRequestStatus) =>
    [...WALLET_KEYS.all, "admin", "svcreqs", businessId, status] as const,
};

export function useMyWallet(businessId: string | null | undefined) {
  return useQuery({
    queryKey: WALLET_KEYS.myWallet(businessId ?? ""),
    queryFn: () => walletApi.getMyWallet(businessId!),
    enabled: !!businessId,
    staleTime: 0,
    refetchOnMount: true,
  });
}

export function useMyWalletTransactions(businessId: string | null | undefined) {
  return useInfiniteQuery({
    queryKey: WALLET_KEYS.myTransactions(businessId ?? ""),
    queryFn: ({ pageParam }: { pageParam: string | undefined }) =>
      walletApi.listMyTransactions(businessId!, { limit: 20, lastKey: pageParam }),
    getNextPageParam: (lastPage) => lastPage.nextKey ?? undefined,
    initialPageParam: undefined as string | undefined,
    enabled: !!businessId,
    staleTime: 0,
    refetchOnMount: true,
  });
}

export function useMyWalletEncashments(businessId: string | null | undefined) {
  return useInfiniteQuery({
    queryKey: WALLET_KEYS.myEncashments(businessId ?? ""),
    queryFn: ({ pageParam }: { pageParam: string | undefined }) =>
      walletApi.listMyEncashments(businessId!, { limit: 20, lastKey: pageParam }),
    getNextPageParam: (lastPage) => lastPage.nextKey ?? undefined,
    initialPageParam: undefined as string | undefined,
    enabled: !!businessId,
    staleTime: 0,
    refetchOnMount: true,
  });
}

export function useRequestEncashment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { businessId: string; amount: number; employeeName?: string }) =>
      walletApi.requestEncashment(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: WALLET_KEYS.all });
    },
  });
}

export function useAdminEncashments(businessId: string | null | undefined, status: EncashmentStatus = "PENDING") {
  return useInfiniteQuery({
    queryKey: WALLET_KEYS.encashments(businessId ?? "", status),
    queryFn: ({ pageParam }: { pageParam: string | undefined }) =>
      walletApi.listEncashments(businessId!, status, { limit: 20, lastKey: pageParam }),
    getNextPageParam: (lastPage) => lastPage.nextKey ?? undefined,
    initialPageParam: undefined as string | undefined,
    enabled: !!businessId,
    staleTime: 0,
    refetchOnMount: true,
  });
}

export function useAdminWalletTransactions(businessId: string | null | undefined, prefix?: string) {
  return useInfiniteQuery({
    queryKey: WALLET_KEYS.transactions(businessId ?? "", prefix),
    queryFn: ({ pageParam }: { pageParam: string | undefined }) =>
      walletApi.listTransactions(businessId!, { prefix, limit: 20, lastKey: pageParam }),
    getNextPageParam: (lastPage) => lastPage.nextKey ?? undefined,
    initialPageParam: undefined as string | undefined,
    enabled: !!businessId,
    staleTime: 0,
    refetchOnMount: true,
  });
}

export function useApproveEncashment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      encashId,
      payload,
    }: {
      encashId: string;
      payload: { userId: string; businessId: string; comments?: string };
    }) => walletApi.approveEncashment(encashId, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: WALLET_KEYS.all });
    },
  });
}

export function useReleaseEncashment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      encashId,
      payload,
    }: {
      encashId: string;
      payload: { userId: string; businessId: string; releasedAt: string; comments?: string };
    }) => walletApi.releaseEncashment(encashId, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: WALLET_KEYS.all });
    },
  });
}

export function useRejectEncashment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      encashId,
      payload,
    }: {
      encashId: string;
      payload: { userId: string; businessId: string; comments?: string };
    }) => walletApi.rejectEncashment(encashId, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: WALLET_KEYS.all });
    },
  });
}

// ── Wallet Advances ───────────────────────────────────────────────────────────

export function useMyWalletAdvances(businessId: string | null | undefined) {
  return useQuery({
    queryKey: WALLET_KEYS.myAdvances(businessId ?? ""),
    queryFn: () => walletApi.listMyWalletAdvances(businessId!),
    enabled: !!businessId,
    staleTime: 0,
    refetchOnMount: true,
  });
}

export function useRequestWalletAdvance() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { businessId: string; amount: number; employeeName?: string }) =>
      walletApi.requestWalletAdvance(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: WALLET_KEYS.all });
    },
  });
}

export function useAdminWalletAdvances(businessId: string | null | undefined, status: WalletAdvanceStatus = "PENDING") {
  return useInfiniteQuery({
    queryKey: WALLET_KEYS.adminAdvances(businessId ?? "", status),
    queryFn: ({ pageParam }: { pageParam: string | undefined }) =>
      walletApi.listAdminWalletAdvances(businessId!, status, { limit: 20, lastKey: pageParam }),
    getNextPageParam: (lastPage) => lastPage.nextKey ?? undefined,
    initialPageParam: undefined as string | undefined,
    enabled: !!businessId,
    staleTime: 0,
    refetchOnMount: true,
  });
}

export function useApproveWalletAdvance() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ advId, payload }: { advId: string; payload: { userId: string; businessId: string; comments?: string } }) =>
      walletApi.approveWalletAdvance(advId, payload),
    onSuccess: () => { qc.invalidateQueries({ queryKey: WALLET_KEYS.all }); },
  });
}

export function useReleaseWalletAdvance() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ advId, payload }: { advId: string; payload: { userId: string; businessId: string; releasedAt: string; comments?: string } }) =>
      walletApi.releaseWalletAdvance(advId, payload),
    onSuccess: () => { qc.invalidateQueries({ queryKey: WALLET_KEYS.all }); },
  });
}

export function useRejectWalletAdvance() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ advId, payload }: { advId: string; payload: { userId: string; businessId: string; comments?: string } }) =>
      walletApi.rejectWalletAdvance(advId, payload),
    onSuccess: () => { qc.invalidateQueries({ queryKey: WALLET_KEYS.all }); },
  });
}

export function useMarkWalletAdvancePaid() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ advId, payload }: { advId: string; payload: { userId: string; businessId: string } }) =>
      walletApi.markWalletAdvancePaid(advId, payload),
    onSuccess: () => { qc.invalidateQueries({ queryKey: WALLET_KEYS.all }); },
  });
}

export function useWalletAdvanceConfig(businessId: string | null | undefined) {
  return useQuery({
    queryKey: WALLET_KEYS.walletConfig(businessId ?? ""),
    queryFn: () => walletApi.getWalletAdvanceConfig(businessId!),
    enabled: !!businessId,
    staleTime: 0,
  });
}

export function useSetWalletAdvanceLimit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { businessId: string; walletAdvanceLimit: number }) =>
      walletApi.setWalletAdvanceLimit(payload),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: WALLET_KEYS.walletConfig(vars.businessId) });
      qc.invalidateQueries({ queryKey: WALLET_KEYS.all });
    },
  });
}

export function useAdminWalletCredits(businessId: string | null | undefined) {
  return useQuery({
    queryKey: WALLET_KEYS.adminCredits(businessId ?? ""),
    queryFn: () => walletApi.listAdminWalletCredits(businessId!).then((r) => r.items),
    enabled: !!businessId,
    staleTime: 0,
  });
}

export function useAddWalletCredit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ businessId, payload }: { businessId: string; payload: { userId: string; businessId: string; amount: number; reason: string } }) =>
      walletApi.addWalletCredit(businessId, payload),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: WALLET_KEYS.adminCredits(vars.businessId) });
      qc.invalidateQueries({ queryKey: WALLET_KEYS.myWallet(vars.businessId) });
      qc.invalidateQueries({ queryKey: WALLET_KEYS.all });
    },
  });
}

// ── Service Requests ──────────────────────────────────────────────────────────

export function useMyServiceRequests(businessId: string | null | undefined) {
  return useQuery({
    queryKey: WALLET_KEYS.myServiceRequests(businessId ?? ""),
    queryFn: () => walletApi.listMyServiceRequests(businessId!).then((r) => r.items),
    enabled: !!businessId,
    staleTime: 0,
  });
}

export function useRequestService() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { businessId: string; serviceId?: string; description: string; notes?: string; amount: number }) =>
      walletApi.requestService(payload),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: WALLET_KEYS.myServiceRequests(vars.businessId) });
    },
  });
}

export function useAdminServiceRequests(businessId: string | null | undefined, status: ServiceRequestStatus = "PENDING") {
  return useQuery({
    queryKey: WALLET_KEYS.adminServiceRequests(businessId ?? "", status),
    queryFn: () => walletApi.adminListServiceRequests(businessId!, status).then((r) => r.items),
    enabled: !!businessId,
    staleTime: 0,
    refetchOnMount: true,
  });
}

export function useApproveServiceRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ requestId, payload }: { requestId: string; payload: { userId: string; businessId: string; commissionAmount: number; reviewNotes?: string } }) =>
      walletApi.approveServiceRequest(requestId, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: WALLET_KEYS.all });
    },
  });
}

export function useRejectServiceRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ requestId, payload }: { requestId: string; payload: { userId: string; businessId: string; reviewNotes?: string } }) =>
      walletApi.rejectServiceRequest(requestId, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: WALLET_KEYS.all });
    },
  });
}

export function useVoidServiceRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ requestId, payload }: { requestId: string; payload: { userId: string; businessId: string } }) =>
      walletApi.voidServiceRequest(requestId, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: WALLET_KEYS.all });
    },
  });
}
